import React, { useState } from 'react';
import { ChevronDown, ChevronUp, CheckCircle, AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const SignatureTypes = () => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState('avancada');
  
  const signatureTypes = [
    {
      id: 'simples',
      name: t('signatureTypes.simple.name'),
      icon: '✍️',
      security: t('signatureTypes.simple.security'),
      useCases: t('signatureTypes.simple.useCases'),
      legalValue: t('signatureTypes.simple.legalValue'),
      description: t('signatureTypes.simple.description'),
      pros: [t('signatureTypes.simple.pro1'), t('signatureTypes.simple.pro2'), t('signatureTypes.simple.pro3')],
      cons: [t('signatureTypes.simple.con1'), t('signatureTypes.simple.con2'), t('signatureTypes.simple.con3')]
    },
    {
      id: 'avancada',
      name: t('signatureTypes.advanced.name'),
      icon: '🔐',
      security: t('signatureTypes.advanced.security'),
      useCases: t('signatureTypes.advanced.useCases'),
      legalValue: t('signatureTypes.advanced.legalValue'),
      recommended: true,
      description: t('signatureTypes.advanced.description'),
      pros: [t('signatureTypes.advanced.pro1'), t('signatureTypes.advanced.pro2'), t('signatureTypes.advanced.pro3'), t('signatureTypes.advanced.pro4'), t('signatureTypes.advanced.pro5')],
      cons: [t('signatureTypes.advanced.con1'), t('signatureTypes.advanced.con2')]
    },
    {
      id: 'qualificada',
      name: t('signatureTypes.qualified.name'),
      icon: '🏛️',
      security: t('signatureTypes.qualified.security'),
      useCases: t('signatureTypes.qualified.useCases'),
      legalValue: t('signatureTypes.qualified.legalValue'),
      description: t('signatureTypes.qualified.description'),
      pros: [t('signatureTypes.qualified.pro1'), t('signatureTypes.qualified.pro2'), t('signatureTypes.qualified.pro3'), t('signatureTypes.qualified.pro4')],
      cons: [t('signatureTypes.qualified.con1'), t('signatureTypes.qualified.con2'), t('signatureTypes.qualified.con3')]
    }
  ];

  return (
    <div className="space-y-6">
      <div className="text-center mb-12">
        <h3 className="text-3xl font-bold text-gray-900 mb-4">{t('signatureTypes.title')}</h3>
        <p className="text-lg text-gray-600">{t('signatureTypes.subtitle')}</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {signatureTypes.map((type, index) => (
          <motion.div
            key={type.id}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            viewport={{ once: true }}
            className={`bg-white rounded-2xl border-2 ${
              type.recommended ? 'border-blue-500 shadow-xl' : 'border-gray-200'
            } overflow-hidden`}
          >
            <div className="p-6">
              <div className="text-5xl mb-4 text-center">{type.icon}</div>
              
              {type.recommended && (
                <div className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-semibold mb-4 text-center">
                  {t('signatureTypes.recommended')}
                </div>
              )}

              <h4 className="text-xl font-bold text-gray-900 mb-2 text-center">{type.name}</h4>
              
              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{t('signatureTypes.security')}:</span>
                  <span className={`font-semibold ${
                    type.security.toLowerCase().includes('max') || type.security.toLowerCase().includes('massima') ? 'text-green-600' : 
                    type.security.toLowerCase().includes('alta') || type.security.toLowerCase().includes('high') ? 'text-blue-600' : 'text-yellow-600'
                  }`}>{type.security}</span>
                </div>
                <div className="text-sm text-gray-600">
                  <strong>{t('signatureTypes.validity')}:</strong> {type.legalValue}
                </div>
              </div>

              <button
                onClick={() => setExpanded(expanded === type.id ? null : type.id)}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-900 py-2 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2"
              >
                <span>{t('signatureTypes.viewDetails')}</span>
                {expanded === type.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </div>

            <AnimatePresence>
              {expanded === type.id && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="bg-gray-50 px-6 pb-6"
                >
                  <p className="text-gray-700 mb-4 leading-relaxed">{type.description}</p>

                  <div className="mb-4">
                    <h5 className="font-semibold text-gray-900 mb-2">{t('signatureTypes.useCases')}:</h5>
                    <p className="text-sm text-gray-600">{type.useCases}</p>
                  </div>

                  <div className="mb-4">
                    <h5 className="font-semibold text-green-700 mb-2 flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4" />
                      <span>{t('signatureTypes.advantages')}:</span>
                    </h5>
                    <ul className="space-y-1">
                      {type.pros.map((pro, i) => (
                        <li key={i} className="text-sm text-gray-700 flex items-start space-x-2">
                          <span className="text-green-600 mt-0.5">•</span>
                          <span>{pro}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h5 className="font-semibold text-yellow-700 mb-2 flex items-center space-x-2">
                      <AlertTriangle className="w-4 h-4" />
                      <span>{t('signatureTypes.limitations')}:</span>
                    </h5>
                    <ul className="space-y-1">
                      {type.cons.map((con, i) => (
                        <li key={i} className="text-sm text-gray-700 flex items-start space-x-2">
                          <span className="text-yellow-600 mt-0.5">•</span>
                          <span>{con}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default SignatureTypes;
